/**
 * The phone's one socket to the assistant's live call.
 *
 * `GET /v1/agent/assistant/live` with the session cookie AND the tenant
 * header, named exactly as every HTTP call inside a business names it: the
 * assistant answers about one business's records, and the server refuses the
 * socket without one. The framing is the setup call's — JSON text frames for
 * control both ways, binary PCM16 for the audio — and so is the race it
 * guards: `start` goes first, whichever of the socket opening or the first
 * microphone frame gets there first.
 *
 * What it sends: `start`, the microphone's audio, `mute`, `stop` (also as the
 * first word of a hand-off, unless the server has already ended the call),
 * `cancel` (Stop, for the running question or a queued one, reaching a
 * draining call too), `interrupt` (the person cut the voice off), `say` (a
 * line that did not come by voice), `view` (the look the call is on), `place`
 * (where the person is now) and `card_result` (what pressing a card did).
 * A capable call (its `start` carries `capabilities`, step 3.1) also sends
 * the version-2 commands: `attention`, `pause` and `resume` (a phone call
 * took the audio and gave it back), `quiet`, `carry_on`, `resume_work` and
 * `attend` (the orb tapped). Each is dropped when the socket is not open.
 *
 * The socket is the only thing this file owns. What a frame means is
 * `assistant-live-wire.ts`, what the sound is is `live-audio.ts`, and what to
 * draw is `use-assistant-live.ts`'s. docs/plans/done/orb-live-call.md, Phase B.
 */
import type {
  AssistantCardResultState,
  AssistantLiveClientMessage,
  AssistantLiveContext,
  AssistantLiveLook,
  AssistantLiveServerMessage,
  LiveClientFrame,
  LiveSensitivity,
} from '@alluzo/shared';

import { apiBaseUrl, TENANT_HEADER } from '@/lib/api';
import { socketHeaders } from '@/lib/client-fetch';
import { assistantLiveWsUrl, readAssistantLiveServerMessage } from '@/lib/assistant-live-wire';

/**
 * How long a call that ended for the conversation waits for what the server
 * still owes. The server closes the socket as soon as the last answer is out;
 * this is only the backstop, past the assistant's own two-minute turn budget.
 */
export const HAND_OFF_MS = 150_000;

/** Where the call lives: the API's own host, over ws. */
export const assistantLiveUrl = (): string => assistantLiveWsUrl(apiBaseUrl);

export type AssistantLiveHandlers = {
  /** One frame of the voice: PCM16 little-endian mono at the output rate. */
  readonly onAudio: (pcm: Uint8Array) => void;
  /** A control frame the phone knows. Anything else never gets here. */
  readonly onMessage: (message: AssistantLiveServerMessage) => void;
  /** The socket is gone, whoever closed it. */
  readonly onClose: (code: number, reason: string) => void;
  /** The socket broke. A close always follows, so this is a note and not a state. */
  readonly onError: () => void;
};

export type AssistantLiveSocket = {
  /** One frame of the microphone. Dropped when the socket is not open. */
  readonly sendAudio: (pcm: Uint8Array) => void;
  /** Close or open the mic without ending the call. */
  readonly mute: (muted: boolean) => void;
  /** Say goodbye, so the server closes the provider session and logs the call. */
  readonly stop: () => void;
  /** Drop the socket where it stands. */
  readonly close: () => void;
  /**
   * Stop: abort the question Coach is answering now, or, with its id, the
   * queued one, which the server then drops before it runs. A socket handed
   * off still sends it, so Stop reaches a draining call (C42).
   */
  readonly cancel: (messageId?: string) => void;
  /** The person cut the voice off: the server drops what the voice was still sending. */
  readonly interrupt: () => void;
  /**
   * A line for Coach that did not come by voice: typed, a chip, Ask again.
   * Answers whether it went out: a socket not open yet, or gone, drops it.
   */
  readonly say: (text: string) => boolean;
  /** Which look the call is on now. */
  readonly view: (look: AssistantLiveLook) => void;
  /** Where the person is now, and the cards pinned there. */
  readonly place: (next: Pick<AssistantLiveContext, 'screen' | 'recordId' | 'cards' | 'view'>) => void;
  /** What pressing a card did, in the card's own words (Phase 11). */
  readonly cardResult: (result: { readonly toolCallId: string; readonly state: AssistantCardResultState; readonly receipt: string | null }) => void;
  /**
   * Say goodbye, and keep listening only for what the server still owes: each
   * `turn` goes to `onTurn`; `asking` and `progress` go to `onWork`, since a
   * drain runs a queued question once the running one lands and says each
   * step (C42); everything else is ignored. `onDone` runs once the socket is
   * gone — the server closed it, or `HAND_OFF_MS` passed, or `close` was
   * called. `sayStop: false` sends no `stop`: the server has already ended
   * the call and is draining it, so the socket only reads what it owes (C7b1,
   * C76).
   */
  readonly handOff: (
    onTurn: (message: Extract<AssistantLiveServerMessage, { type: 'turn' }>) => void,
    onDone: () => void,
    options?: {
      readonly sayStop?: boolean;
      readonly onWork?: (message: Extract<AssistantLiveServerMessage, { type: 'asking' | 'progress' }>) => void;
    },
  ) => void;
};

/**
 * The phone's socket, with the version-2 commands a capable call sends. The
 * web's socket (`apps/web/src/features/voice/live-socket.ts`) implements
 * `AssistantLiveSocket` alone, so these stay apart from it.
 */
export type AssistantLiveCallSocket = AssistantLiveSocket & {
  /** Whether the person is looking, the app's state, the cards in view and the last touch. */
  readonly attention: (attention: Omit<Extract<LiveClientFrame, { type: 'attention' }>, 'type'>) => void;
  /** The system took the audio (a phone call, a route change): the call pauses and stays. */
  readonly pause: (why: Extract<LiveClientFrame, { type: 'pause' }>['why']) => void;
  /** The audio is back and the person tapped Resume. */
  readonly resume: () => void;
  /** Quiet on or off. */
  readonly quiet: (on: boolean) => void;
  /** Carry on past the 30-minute roll-over. */
  readonly carryOn: () => void;
  /** Bring back the parked request. */
  readonly resumeWork: () => void;
  /** The orb was tapped: what comes next is for OS. */
  readonly attend: () => void;
  /** "Was that for me?" was tapped: the utterance it named was for OS (option B, step 5). */
  readonly claim: (utteranceId: string) => void;
  /** The sensitivity changed in Account while live (option B, step 6). */
  readonly sensitivity: (level: LiveSensitivity) => void;
  /** The client's voice activity: someone speaking, near the device, and the level. */
  readonly vad: (vad: Omit<Extract<LiveClientFrame, { type: 'vad' }>, 'type'>) => void;
  /** How much of OS's turn was heard, at a cut (`paused`: the person's words paused it) and at the turn's end. */
  readonly playback: (playback: Omit<Extract<LiveClientFrame, { type: 'playback' }>, 'type'>) => void;
  /** The echo detector dropped the call to half duplex, or earphones brought it back to full. */
  readonly duplex: (mode: 'full' | 'half') => void;
  /** The setup seat: the app's own progress, for the voice to know of (contract rule 6). */
  readonly appNote: (kind: Extract<LiveClientFrame, { type: 'app_note' }>['kind'], text: string) => void;
};

/**
 * React Native's WebSocket takes `{ headers }` in a third constructor
 * argument; the global type visible here stops at two. The cast says what the
 * platform actually accepts, exactly as `sync-events.ts` does.
 */
type WebSocketCtor = new (
  url: string,
  protocols: string | string[] | null,
  options: { headers: Record<string, string> },
) => WebSocket;

/**
 * The socket. `tenantId` null is the setup seat (`seat: 'setup'`, step 5.3):
 * the setup over a business that does not exist yet opens on `?seat=setup`
 * with no tenant header, as the server's seat rules ask.
 */
export const connectAssistantLive = (
  cookie: string,
  tenantId: string | null,
  context: AssistantLiveContext,
  handlers: AssistantLiveHandlers & { readonly seat?: 'setup' },
): AssistantLiveCallSocket => {
  const url = handlers.seat === undefined ? assistantLiveUrl() : `${assistantLiveUrl()}?seat=${handlers.seat}`;
  const ws = new (WebSocket as unknown as WebSocketCtor)(url, null, {
    headers: socketHeaders(tenantId === null ? { cookie } : { cookie, [TENANT_HEADER]: tenantId }),
  });
  // The voice arrives as bytes, not as a Blob read asynchronously: by the
  // time a Blob resolved, the sound would be late.
  ws.binaryType = 'arraybuffer';

  /** Whether the frame went out: one sent while the socket is not open is dropped. */
  const send = (message: AssistantLiveClientMessage): boolean => {
    if (ws.readyState !== WebSocket.OPEN) return false;
    ws.send(JSON.stringify(message));
    return true;
  };

  // The recorder is already running when this socket is created. A native
  // frame can reach JS in the same tick the socket becomes OPEN, before
  // `onopen` runs, and the server closes 1008 on any binary before `start`:
  // whichever of `onopen` or `sendAudio` runs first is the one that sends it.
  let started = false;
  const sendStart = (): void => {
    if (started || ws.readyState !== WebSocket.OPEN) return;
    send({ type: 'start', context });
    started = true;
  };

  ws.onopen = () => sendStart();

  ws.onmessage = (event) => {
    const data: unknown = event.data;
    if (data instanceof ArrayBuffer) {
      handlers.onAudio(new Uint8Array(data));
      return;
    }
    const message = readAssistantLiveServerMessage(data);
    if (message !== null) handlers.onMessage(message);
  };

  ws.onclose = (event) => {
    const closed = event as unknown as { code?: number; reason?: string };
    handlers.onClose(typeof closed.code === 'number' ? closed.code : 0, typeof closed.reason === 'string' ? closed.reason : '');
  };

  ws.onerror = () => handlers.onError();

  let backstop: ReturnType<typeof setTimeout> | null = null;
  let done: (() => void) | null = null;
  const over = (): void => {
    if (backstop !== null) clearTimeout(backstop);
    backstop = null;
    const held = done;
    done = null;
    held?.();
  };

  const close = (): void => {
    // The handlers belong to a call that is over; a close event now would
    // walk the screen back into a state it has left.
    ws.onclose = null;
    ws.onerror = null;
    ws.onmessage = null;
    ws.close();
    over();
  };

  return {
    sendAudio: (pcm) => {
      if (ws.readyState !== WebSocket.OPEN) return;
      sendStart();
      if (!started) return;
      ws.send(pcm);
    },
    mute: (muted) => send({ type: 'mute', muted }),
    stop: () => send({ type: 'stop' }),
    close,
    cancel: (messageId) => send(messageId === undefined ? { type: 'cancel' } : { type: 'cancel', messageId }),
    interrupt: () => send({ type: 'interrupt' }),
    say: (text) => send({ type: 'say', text }),
    view: (look) => send({ type: 'view', look }),
    place: ({ screen, recordId, cards, view }) =>
      send({
        type: 'place',
        screen,
        ...(recordId === undefined ? {} : { recordId }),
        ...(cards === undefined ? {} : { cards }),
        ...(view === undefined ? {} : { view }),
      }),
    cardResult: (result) => send({ type: 'card_result', ...result }),
    attention: (attention) => send({ type: 'attention', ...attention }),
    pause: (why) => send({ type: 'pause', why }),
    resume: () => send({ type: 'resume' }),
    quiet: (on) => send({ type: 'quiet', on }),
    carryOn: () => send({ type: 'carry_on' }),
    resumeWork: () => send({ type: 'resume_work' }),
    vad: (vad) => send({ type: 'vad', ...vad }),
    playback: (playback) => send({ type: 'playback', ...playback }),
    duplex: (mode) => send({ type: 'duplex', mode }),
    appNote: (kind, text) => send({ type: 'app_note', kind, text }),
    attend: () => send({ type: 'attend' }),
    claim: (utteranceId) => send({ type: 'claim', utteranceId }),
    sensitivity: (level) => send({ type: 'sensitivity', level }),
    handOff: (onTurn, onDone, options) => {
      done = onDone;
      // A socket that never opened carried no question: nothing is owed.
      if (ws.readyState !== WebSocket.OPEN) {
        close();
        return;
      }
      if (options?.sayStop !== false) send({ type: 'stop' });
      ws.onerror = null;
      ws.onmessage = (event) => {
        if (event.data instanceof ArrayBuffer) return;
        const message = readAssistantLiveServerMessage(event.data);
        if (message?.type === 'turn') onTurn(message);
        else if (message?.type === 'asking' || message?.type === 'progress') options?.onWork?.(message);
      };
      ws.onclose = () => over();
      backstop = setTimeout(close, HAND_OFF_MS);
    },
  };
};
